import { useEffect, useSyncExternalStore } from "react";
import { db, type MetaRecord } from "./db";
import { getAppearance, subscribeConfig } from "./appConfig";

export type Theme = "light" | "dark";

export interface Settings {
  animations: boolean;
  journeyLimit: number;
  theme: Theme; // the RESOLVED theme actually applied (from themePref + the admin default mode)
  themePref: Theme | null; // the visitor's EXPLICIT choice; null = follow the admin default
  triedFeatures: string[];
}

function prefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

const DEFAULT: Settings = {
  animations: !prefersReducedMotion(),
  journeyLimit: 20,
  theme: prefersDark() ? "dark" : "light",
  themePref: null,
  triedFeatures: [],
};

let current: Settings = DEFAULT;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

// Resolution chain: the visitor's explicit choice wins; else the admin default mode; 'system'
// follows the OS. Returns the concrete light/dark to apply.
function resolveTheme(): Theme {
  if (current.themePref === "light" || current.themePref === "dark") return current.themePref;
  const mode = getAppearance().mode;
  if (mode === "light" || mode === "dark") return mode;
  return prefersDark() ? "dark" : "light";
}

function emit() {
  for (const l of listeners) l();
}

function applyToDocument(s: Settings) {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.animations = s.animations ? "on" : "off";
  document.documentElement.dataset.theme = s.theme;
  // Admin theme-color override -> --accent (accent-soft/line/text re-derive via color-mix). Null
  // removes the override so the stylesheet default applies.
  const color = getAppearance().themeColor;
  if (color) document.documentElement.style.setProperty("--accent", color);
  else document.documentElement.style.removeProperty("--accent");
}

// Re-resolve when a live theme input changes underneath us: an admin publish (config change) or —
// when the resolved source is the OS — a system light/dark flip.
function reapply() {
  current = { ...current, theme: resolveTheme() };
  applyToDocument(current);
  emit();
}
subscribeConfig(reapply);
if (typeof window !== "undefined") {
  window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener?.("change", reapply);
}

// Hydrate `current` from Dexie exactly once. A shared promise (not an `initialized` flag) is essential:
// concurrent callers — e.g. a markFeatureTried() fired from a component mounting during initial load —
// must await the SAME in-flight read, or they'd run against the still-default `current` and then get
// clobbered when the read resolves (this caused the journey checklist to miss early feature marks).
function loadOnce(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const stored: MetaRecord | undefined = await db.meta.get("meta");
      if (stored) {
        const pref: Theme | null =
          stored.themePref === "light" || stored.themePref === "dark"
            ? stored.themePref
            : stored.theme === "light" || stored.theme === "dark"
              ? stored.theme // migrate a legacy resolved theme as the visitor's explicit choice
              : null;
        current = {
          animations:
            typeof stored.animations === "boolean" ? stored.animations : current.animations,
          journeyLimit:
            typeof stored.journeyLimit === "number" && stored.journeyLimit > 0
              ? stored.journeyLimit
              : current.journeyLimit,
          theme: current.theme,
          themePref: pref,
          triedFeatures: Array.isArray(stored.triedFeatures)
            ? stored.triedFeatures
            : current.triedFeatures,
        };
      }
    } catch {
      // ignore — Dexie may not be available
    }
    current = { ...current, theme: resolveTheme() };
    applyToDocument(current);
    emit();
  })();
  return loadPromise;
}

export function getSettings(): Settings {
  return current;
}

export async function setAnimations(on: boolean) {
  current = { ...current, animations: on };
  applyToDocument(current);
  emit();
  try {
    const existing = (await db.meta.get("meta")) ?? { id: "meta" as const, visitorUuid: "" };
    await db.meta.put({ ...existing, animations: on });
  } catch {
    // ignore
  }
}

// Set the visitor's EXPLICIT theme choice (the topbar toggle). Pass null to clear the override and
// follow the admin default mode again.
export async function setTheme(pref: Theme | null) {
  current = { ...current, themePref: pref };
  current = { ...current, theme: resolveTheme() };
  applyToDocument(current);
  emit();
  try {
    const existing = (await db.meta.get("meta")) ?? { id: "meta" as const, visitorUuid: "" };
    const next = { ...existing, themePref: pref ?? undefined };
    delete next.theme; // drop the legacy resolved-theme field; themePref is now the source of truth
    await db.meta.put(next);
  } catch {
    // ignore
  }
}

export async function setJourneyLimit(limit: number) {
  const clamped = Math.max(5, Math.min(500, Math.round(limit)));
  current = { ...current, journeyLimit: clamped };
  emit();
  try {
    const existing = (await db.meta.get("meta")) ?? { id: "meta" as const, visitorUuid: "" };
    await db.meta.put({ ...existing, journeyLimit: clamped });
  } catch {
    // ignore
  }
}

/**
 * Mark an app feature as tried (idempotent) — drives the "Your journey" feature checklist. Safe to
 * call from anywhere a feature is used; loads settings first so an early call isn't lost.
 */
export async function markFeatureTried(id: string) {
  await loadOnce();
  if (current.triedFeatures.includes(id)) return;
  current = { ...current, triedFeatures: [...current.triedFeatures, id] };
  emit();
  try {
    const existing = (await db.meta.get("meta")) ?? { id: "meta" as const, visitorUuid: "" };
    await db.meta.put({ ...existing, triedFeatures: current.triedFeatures });
  } catch {
    // ignore
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  void loadOnce();
  return () => {
    listeners.delete(cb);
  };
}

export function useSettings(): Settings {
  return useSyncExternalStore(subscribe, getSettings, getSettings);
}

export function useTheme(): Theme {
  const s = useSettings();
  return s.theme;
}

export function useInitSettings() {
  useEffect(() => {
    void loadOnce();
  }, []);
}
